import { LitElement, html, css, svg } from 'lit'
import { customElement, property } from 'lit/decorators.js'

type StrokePattern = 'solid' | 'dashed' | 'dotted' | 'dash-dot' | 'long-dash'

const STROKE_STYLES: { pattern: StrokePattern; label: string; dashArray: string }[] = [
  { pattern: 'solid', label: 'Solid', dashArray: '' },
  { pattern: 'dashed', label: 'Dashed', dashArray: '8,6' },
  { pattern: 'dotted', label: 'Dotted', dashArray: '2,4' },
  { pattern: 'dash-dot', label: 'Dash Dot', dashArray: '8,4,2,4' },
  { pattern: 'long-dash', label: 'Long Dash', dashArray: '16,8' },
]

export function patternToDashArray(pattern: StrokePattern): string {
  return STROKE_STYLES.find(s => s.pattern === pattern)?.dashArray ?? ''
}

@customElement('stroke-style-dropdown')
export class StrokeStyleDropdown extends LitElement {
  static override styles = css`
    :host {
      position: absolute;
      z-index: 2000;
    }

    .dropdown {
      background: rgba(30, 41, 59, 0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-radius: 12px;
      padding: 8px 0;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      width: 200px;
    }

    .style-option {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 16px;
      cursor: pointer;
      background: none;
      border: none;
      width: 100%;
      color: rgba(255, 255, 255, 0.85);
      font-size: 13px;
      transition: background 0.15s ease;
      box-sizing: border-box;
    }

    .style-option:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .style-option.selected {
      color: white;
      background: rgba(255, 255, 255, 0.15);
    }

    .stroke-preview {
      flex-shrink: 0;
    }

    .style-label {
      font-weight: 500;
    }
  `

  @property({ type: String })
  selected: StrokePattern = 'solid'

  private selectStyle(pattern: StrokePattern): void {
    this.dispatchEvent(
      new CustomEvent('style-changed', {
        detail: { pattern, dashArray: patternToDashArray(pattern) },
        bubbles: true,
      })
    )
  }

  private renderStrokePreview(dashArray: string): unknown {
    return svg`
      <svg class="stroke-preview" width="60" height="12" viewBox="0 0 60 12">
        <line
          x1="0" y1="6" x2="60" y2="6"
          stroke="rgba(255, 255, 255, 0.85)"
          stroke-width="2.5"
          stroke-dasharray=${dashArray || 'none'}
          stroke-linecap="round"
        />
      </svg>
    `
  }

  override render(): unknown {
    return html`
      <div class="dropdown">
        ${STROKE_STYLES.map(
          s => html`
            <button
              class="style-option ${this.selected === s.pattern ? 'selected' : ''}"
              @click=${(): void => this.selectStyle(s.pattern)}
            >
              ${this.renderStrokePreview(s.dashArray)}
              <span class="style-label">${s.label}</span>
            </button>
          `
        )}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'stroke-style-dropdown': StrokeStyleDropdown
  }
}
